import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import cx from 'classnames';
import { clamp } from '@THC/utils/helpers';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { InputOptionCard } from '@motorway/mw-highway-code/pre-alpha';

import VehiclePaymentTexts from 'Components/pages/VehiclePayment/VehiclePayment.texts.json';
import { LOGO_BY_PROVIDER } from 'Components/pages/VehiclePayment/VehiclePaymentsPaymentOptions/providers/StockFunding/helpers';
import BenefitCardStyles from 'Components/StaticPages/ProductPageComponents/BenefitCards/BenefitCard.module.scss';
import SectionHeading from 'Components/StaticPages/ProductPageComponents/SectionHeading/SectionHeading';
import SectionHeadingStyles from 'Components/StaticPages/ProductPageComponents/SectionHeading/SectionHeading.module.scss';
import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayPayPageEvents } from 'Services/analytics/events/paymentEvents/motorwayPayPageEvents';
import type { MarketableProvider } from 'Services/motorway/services/paymentOptions/paymentOptions.types';
import { SECOND } from 'Utilities/consts';
import { withViewTransition } from 'Utilities/viewTransitions';

import LocalTexts from '../DiscoverMWPay.texts.json';

import styles from './StockFundsSection.module.scss';

const TRANSITION_DELAY = 15 * SECOND;
const TRANSITION_INTERSECTION_THRESHOLD = 0.1;

const TRANSITION_STYLE = `
:root {
	view-transition-name: none;
}

::view-transition {
	pointer-events: none;
}

::view-transition-old(main-content),
::view-transition-new(main-content) {
	animation-duration: 0.6s;
	animation-fill-mode: both;
	animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
}

::view-transition-old(main-content) {
	animation-name: subtle-out;
}

::view-transition-new(main-content) {
	animation-name: subtle-in;
}

@keyframes subtle-out {
	from {
		opacity: 1;
		transform: translateY(0) scale(1);
	}

	to {
		opacity: 0;
		transform: translateY(-12px) scale(0.96);
	}
}

@keyframes subtle-in {
	from {
		opacity: 0;
		transform: translateY(16px) scale(0.96);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}`;

// Order here determines the display order of the payment methods
const MARKETABLE_PROVIDERS: MarketableProvider[] = [
	'stock-funding-lecapital',
	'stock-funding-nextgear',
	'wallet-modulr',
];

const LocalT = new MDText(LocalTexts);
const VehiclePaymentT = new MDText(VehiclePaymentTexts);

type Rotator<T = MarketableProvider> = Generator<T, T, T>;
function* rotateProviders(): Rotator {
	let index = 0;

	while (true) {
		yield MARKETABLE_PROVIDERS[index];
		index = (index + 1) % MARKETABLE_PROVIDERS.length;
	}
}

export const StockFundsSection: React.FC = () => {
	const rotator = useRef(rotateProviders());
	const [selected, setSelected] = useState<MarketableProvider>(() => rotator.current.next().value);
	const timeout = useRef<ReturnType<typeof setTimeout>>();
	const progressBarRef = useRef<HTMLDivElement>(null);
	const rotationTimeStartAt = useRef<number | null>(null);
	const rotationTimeRemaining = useRef<number | null>(null);

	const currentSlideText = LocalTexts.stockFunds.paymentOptions[selected];
	const currentTitle = LocalT.translate(currentSlideText.title);
	const currentContent = LocalT.translate(currentSlideText.content);
	let currentCtaText: React.ReactNode | null = null;
	let currentCtaLink: string | null = null;

	if ('cta' in currentSlideText) {
		currentCtaText = LocalT.translate(currentSlideText.cta.text);
		currentCtaLink = currentSlideText.cta.link;
	}

	const onChange = useCallback((key: MarketableProvider) => {
		void withViewTransition(() => setSelected(key));
	}, []);

	const clearRotation = () => {
		clearTimeout(timeout.current);
	};

	const setAnimationState = (state: 'running' | 'paused') => {
		if (progressBarRef.current?.style) {
			progressBarRef.current.style.setProperty('--dwa-progress-animation-play-state', state);
		}
	};

	const scheduleNextTransition = useCallback(
		(delay: number = TRANSITION_DELAY) => {
			rotationTimeStartAt.current = Date.now();
			rotationTimeRemaining.current = delay;

			timeout.current = setTimeout(() => {
				const nextProvider = rotator.current.next().value;
				onChange(nextProvider);

				// Schedule the next transition with full delay
				scheduleNextTransition();
			}, delay);
		},
		[onChange],
	);

	const startRotation = useCallback(() => {
		const delay = rotationTimeRemaining.current ?? TRANSITION_DELAY;

		setAnimationState('running');
		scheduleNextTransition(delay);
	}, [scheduleNextTransition]);

	const pauseRotation = useCallback(() => {
		if (rotationTimeStartAt.current) {
			const elapsed = Date.now() - rotationTimeStartAt.current;
			const baseTime = rotationTimeRemaining.current ?? TRANSITION_DELAY;
			const remaining = baseTime - elapsed;
			rotationTimeRemaining.current = clamp(remaining, 0, TRANSITION_DELAY);
		}

		setAnimationState('paused');

		clearRotation();
	}, []);

	const onClick = (key: MarketableProvider) => {
		motorwayPayPageEvents<{
			label: string;
		}>({
			customData: {
				label: key,
			},
			name: 'Payment option clicked',
			schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
			version: SNOWPLOW_EVENTS.CLICK.VERSION,
		});

		clearRotation();

		rotator.current = rotateProviders();
		const clickedIndex = MARKETABLE_PROVIDERS.indexOf(key);

		// Advance the generator to the clicked index
		// This ensures that the next auto-rotation continues from the clicked item
		for (let i = 0; i <= clickedIndex; i++) {
			rotator.current.next();
		}

		startRotation();
	};

	const { ref } = useInView({
		fallbackInView: true,
		onChange(visible) {
			if (visible) {
				startRotation();
			} else {
				pauseRotation();
			}
		},
		threshold: TRANSITION_INTERSECTION_THRESHOLD,
	});

	useEffect(() => {
		return clearRotation;
	}, []);

	return (
		<>
			<style dangerouslySetInnerHTML={{ __html: TRANSITION_STYLE }} />
			<SectionHeading
				heading={LocalT.translate(LocalTexts.stockFunds.heading)}
				preheading={LocalT.translate(LocalTexts.stockFunds.preheading)}
			/>
			<section ref={ref} className={styles.wrapper} data-testid="stock-funds-section">
				<aside className={cx(styles.aside, BenefitCardStyles.card)}>
					<h4 className={cx(styles.heading, SectionHeadingStyles.heading)}>
						{LocalT.translate(LocalTexts.stockFunds.paymentOptions.title)}
					</h4>
					{MARKETABLE_PROVIDERS.map((provider) => (
						<InputOptionCard
							key={provider}
							fullWidth
							data-provider={provider}
							formProps={{
								input: {
									checked: provider === selected,
									name: 'stockFundingOption',
									onChange: onChange.bind(null, provider),
									onClick: onClick.bind(null, provider),
								},
							}}
							heading={VehiclePaymentT.translate(`views.paymentOptions.providers.${provider}.title`)}
							icon={LOGO_BY_PROVIDER[provider]}
							id={`option-${provider}`}
							type="radio"
						/>
					))}
				</aside>
				<main className={cx(styles.main, BenefitCardStyles.card)}>
					<div className={styles.cell}>
						<h3 className={cx(SectionHeadingStyles.preheading)}>{currentTitle}</h3>
						<p>{currentContent}</p>
					</div>
					<div className={styles.cell}>
						{currentCtaText && currentCtaLink && (
							<Button
								as="a"
								href={currentCtaLink}
								icon="arrow"
								label={currentCtaText}
								onClick={() => {
									motorwayPayPageEvents<{
										label: string;
									}>({
										customData: {
											label: selected,
										},
										name: 'Payment option affiliate CTA clicked',
										schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
										version: SNOWPLOW_EVENTS.CLICK.VERSION,
									});
								}}
								target={currentCtaLink.startsWith('#') ? undefined : '_blank'} // Open in new tab unless it's in-page link
								variant="primary"
							/>
						)}
					</div>
					<div
						key={selected} // Restart animation on provider change
						ref={progressBarRef}
						aria-hidden="true"
						className={styles.progressBar}
						style={{ '--dwa-progress-animation-duration': `${TRANSITION_DELAY}ms` } as React.CSSProperties}
					/>
				</main>
			</section>
		</>
	);
};
